import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/contexts/auth-context";
import { API_BASE } from "@/config/api";
import type { Asset } from "@/schemas";
import { Loader2, Globe, Moon, Sun } from "lucide-react";
import { useTheme } from "@/components/providers/theme-provider";
import { cn } from "@/lib/utils";

interface CurrencySelectionModalProps {
  open: boolean;
  onClose: () => void;
  isRequired?: boolean;
  showThemeSelector?: boolean;
}

type AppTheme = "light" | "dark";

export function CurrencySelectionModal({
  open,
  onClose,
  isRequired = false,
  showThemeSelector = false,
}: CurrencySelectionModalProps) {
  const { updateUser } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [selectedTheme, setSelectedTheme] = useState<AppTheme | "">("");
  const [fiatOptions, setFiatOptions] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !showThemeSelector) return;

    if (isRequired) {
      setSelectedTheme("");
      return;
    }

    const fallbackTheme = theme === "system" ? resolvedTheme : theme;
    setSelectedTheme(fallbackTheme);
  }, [open, showThemeSelector, isRequired, theme, resolvedTheme]);

  // Fetch fiat assets when modal opens
  useEffect(() => {
    if (!open) return;

    const fetchFiatAssets = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchWithAuth(
          `${API_BASE}/assets?type=fiat`,
          { method: "GET" }
        );
        if (response.ok) {
          const data = await response.json();
          setFiatOptions(data.results || []);
        } else {
          setError("Failed to load currencies");
        }
      } catch (err) {
        console.error("Error fetching fiat assets:", err);
        setError("Failed to load currencies");
      } finally {
        setLoading(false);
      }
    };

    fetchFiatAssets();
  }, [open]);

  const handleSave = async () => {
    if (!selectedCurrency) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`${API_BASE}/users/me`, {
        method: "PUT",
        body: JSON.stringify({ base_currency_id: selectedCurrency }),
      });

      if (response.ok) {
        await response.json();
        // Update the user in context
        updateUser({ base_currency_id: selectedCurrency });
        if (showThemeSelector && selectedTheme) {
          setTheme(selectedTheme);
        }
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save currency preference");
      }
    } catch (err) {
      console.error("Error saving currency:", err);
      setError("Failed to save currency preference");
    } finally {
      setSaving(false);
    }
  };

  const selectedFiatAsset = fiatOptions.find(
    (f) => f.asset_id === selectedCurrency
  );

  return (
    <Dialog open={open} onOpenChange={isRequired ? undefined : onClose}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={isRequired ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={isRequired ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-primary/10">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-xl">
              {isRequired ? "Welcome to WealthScale!" : "Base Currency Settings"}
            </DialogTitle>
          </div>
          <DialogDescription className="text-base">
            {isRequired
              ? "Please choose your base currency and your theme. These preferences are used across your portfolio views."
              : "Update your base currency preference. This affects how your portfolio values are displayed."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="currency-select" className="text-sm font-medium">
                  Select your base currency
                </Label>
                <Select
                  value={selectedCurrency}
                  onValueChange={setSelectedCurrency}
                >
                  <SelectTrigger id="currency-select" className="w-full">
                    <SelectValue placeholder="Choose a currency..." />
                  </SelectTrigger>
                  <SelectContent>
                    {fiatOptions.map((fiat) => (
                      <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{fiat.symbol}</span>
                          <span className="text-muted-foreground">
                            - {fiat.name}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {showThemeSelector && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Choose your theme</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant={selectedTheme === "light" ? "default" : "outline"}
                      onClick={() => setSelectedTheme("light")}
                      className={cn(
                        "justify-start gap-2",
                        selectedTheme !== "light" && "text-muted-foreground"
                      )}
                    >
                      <Sun className="h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      type="button"
                      variant={selectedTheme === "dark" ? "default" : "outline"}
                      onClick={() => setSelectedTheme("dark")}
                      className={cn(
                        "justify-start gap-2",
                        selectedTheme !== "dark" && "text-muted-foreground"
                      )}
                    >
                      <Moon className="h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>
              )}

              {selectedFiatAsset && (
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <p className="text-sm text-muted-foreground">
                    Your portfolio values will be displayed in{" "}
                    <span className="font-semibold text-foreground">
                      {selectedFiatAsset.name} ({selectedFiatAsset.symbol})
                    </span>
                  </p>
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3">
          {!isRequired && (
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={
              !selectedCurrency ||
              saving ||
              loading ||
              (showThemeSelector && !selectedTheme)
            }
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
